import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchProperties, PublicProperty } from '../../../../pro-admin/lib/api';

export const PropertyCategories: React.FC<{ initialCategory?: string }> = ({ initialCategory }) => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(initialCategory || 'boats');
  const [properties, setProperties] = useState<Record<string, PublicProperty[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initialCategory && initialCategory !== activeCategory) {
      setActiveCategory(initialCategory);
    }
  }, [initialCategory]);

  const categories = [
    { id: 'boats', name: 'Boats', icon: 'ri-ship-line' },
    { id: 'apartments', name: 'Apartments', icon: 'ri-building-line' },
    { id: 'buildings', name: 'Buildings', icon: 'ri-building-2-line' },
    { id: 'beach-houses', name: 'Beach Houses', icon: 'ri-home-smile-line' },
    { id: 'furniture', name: 'Furniture', icon: 'ri-sofa-line' },
    { id: 'cars', name: 'Cars', icon: 'ri-car-line' },
  ];

  const categoryToSlug = (cat?: PublicProperty['category']): string | null => {
    switch (cat) {
      case 'Boat':
        return 'boats';
      case 'Apartment':
        return 'apartments';
      case 'Building & Selling':
        return 'buildings';
      case 'Beach House':
        return 'beach-houses';
      case 'Furniture':
        return 'furniture';
      case 'Car':
        return 'cars';
      default:
        return null;
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const list = await fetchProperties();
        const organized: Record<string, PublicProperty[]> = {};
        for (const p of list) {
          const slug = categoryToSlug(p.category);
          if (!slug) continue;
          if (!organized[slug]) organized[slug] = [];
          organized[slug].push(p);
        }
        setProperties(organized);
      } catch (e: any) {
        setError(e.message || 'Failed to fetch properties');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const formatPrice = (p: PublicProperty): string => {
    const value = typeof p.display_price === 'number' ? p.display_price : 0;
    return value ? `${value.toLocaleString()}` : '';
  };

  const computedFeatures = (p: PublicProperty): string[] => {
    return [
      p.bedrooms ? `${p.bedrooms} bedrooms` : undefined,
      p.bathrooms ? `${p.bathrooms} bathrooms` : undefined,
      p.area_sqft ? `${p.area_sqft} sqft` : undefined,
      p.category ? `${p.category}` : undefined,
    ].filter(Boolean) as string[];
  };
const fallbackRatings = [3, 4, 4.5];
const pickFallbackRating = (id?: string) => {
  const base = (id || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return fallbackRatings[base % fallbackRatings.length];
};
const getDisplayRating = (p: PublicProperty) => (typeof p.rating === 'number' && p.rating > 0) ? p.rating : pickFallbackRating(p.id);

  const handlePropertyClick = (prop: PublicProperty) => {
    navigate(`/property/${activeCategory}/${prop.id}`, { state: { property: prop } });
  };

  const availabilityBadge = (status?: PublicProperty['status']) => {
    const label = status === 'booked' ? 'Booked' : status === 'sold_out' ? 'Not Available' : 'Available';
    const color = status === 'booked' ? 'bg-yellow-500' : status === 'sold_out' ? 'bg-red-600' : 'bg-green-600';
    return (
      <span className={`inline-flex items-center ${color} text-white text-[10px] sm:text-xs font-semibold px-2 py-1 rounded-full shadow-md`}>{label}</span>
    );
  };

  // Badge to show pricing type: Per Night, Per Day, or For Sale
  const priceTypeBadge = (label?: PublicProperty['display_price_label']) => {
    const text = label === 'full' ? 'For Sale' : label === 'day' ? 'Per Day' : label === 'night' ? 'Per Night' : '';
    if (!text) return null;
    const color = label === 'full' ? 'bg-red-600' : label === 'day' ? 'bg-blue-600' : label === 'night' ? 'bg-teal-600' : '';
    return (
      <span className={`inline-flex items-center ${color} text-white text-[10px] sm:text-xs font-semibold px-2 py-1 rounded-full shadow-md`}>{text}</span>
    );
  };

  if (loading) {
    return <div className="text-center py-20">Loading properties...</div>;
  }

  // Show helpful empty state if API returns nothing
  const activeList = properties[activeCategory] || [];
  const allProperties: PublicProperty[] = Object.values(properties).flat();
  // Removed early return so layout remains visible when empty

  if (error) {
    return <div className="text-center py-20 text-red-500">Error: {error}</div>;
  }

  return (
    <section className="py-6 sm:py-8 md:py-12 lg:py-16 xl:py-20 bg-gray-50">
      <div className="w-full px-2 sm:px-4 md:px-6 lg:px-8 xl:px-12">
        <div className="text-center mb-4 sm:mb-6 md:mb-8 lg:mb-12">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-2 sm:mb-3 md:mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Explore Our Properties
          </h2>
          <p className="text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl text-gray-600 max-w-3xl mx-auto px-2">
            Discover premium real estate, luxury boats, and exquisite furniture collections
          </p>
        </div>

        {/* Category Tabs - Ultra Mobile Responsive */}
        <div className="mb-4 sm:mb-6 md:mb-8 lg:mb-12">
          <div className="flex overflow-x-auto scrollbar-hide pb-2 sm:pb-0 -mx-2 px-2">
            <div className="flex space-x-1 sm:space-x-2 md:space-x-3 lg:space-x-4 min-w-max sm:min-w-0 sm:justify-center sm:w-full">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`flex items-center space-x-1 sm:space-x-1.5 md:space-x-2 px-2 sm:px-3 md:px-4 lg:px-6 py-1.5 sm:py-2 md:py-2.5 lg:py-3 rounded-full text-xs sm:text-sm md:text-base font-medium transition-all duration-300 whitespace-nowrap ${
                    activeCategory === category.id
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                >
                  <i className={`${category.icon} text-xs sm:text-sm md:text-base`}></i>
                  <span className="hidden xs:inline sm:inline">{category.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Property Grid - Ultra Mobile Responsive */}
        <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3 md:gap-4 lg:gap-6 xl:gap-8">
          {activeList.length === 0 ? (
            <div className="col-span-1 xs:col-span-2 lg:col-span-3 text-center py-10">
              <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900">No property listed</h3>
            </div>
          ) : (
            activeList.map((property) => {
              const features = computedFeatures(property).slice(0, 4);
              const priceText = formatPrice(property);
              return (
                <div
                  key={property.id}
                  onClick={() => handlePropertyClick(property)}
                  className="bg-white rounded-lg sm:rounded-xl md:rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-105 overflow-hidden"
                  data-product-shop
                >
                  <div className="relative">
                    <img
                      src={property.image_url}
                      alt={property.title}
                      className="w-full h-32 xs:h-36 sm:h-40 md:h-48 lg:h-56 xl:h-64 object-cover object-top"
                    />
                    <div className="absolute top-2 left-2 sm:top-3 sm:left-3 md:top-4 md:left-4">
                      {availabilityBadge(property.status)}
                    </div>
                    <div className="absolute top-2 sm:top-3 md:top-4 right-2 sm:right-3 md:right-4 bg-white/90 backdrop-blur-sm rounded-full px-1.5 sm:px-2 md:px-3 py-0.5 sm:py-1 md:py-1.5">
                      <div className="flex items-center space-x-0.5 sm:space-x-1">
                        <i className="ri-star-fill text-yellow-400 text-xs sm:text-sm"></i>
                        <span className="text-xs sm:text-sm font-medium text-gray-900">{getDisplayRating(property)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-2 sm:p-3 md:p-4 lg:p-5 xl:p-6">
                    <h3 className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-bold text-gray-900 mb-1 sm:mb-2 line-clamp-2">{property.title}</h3>
                    {/* Description snippet with fallback */}
                    <p className="text-[11px] sm:text-xs md:text-sm text-gray-600 mb-2 line-clamp-2">
                      {property.description || 'No description available.'}
                    </p>
                    {/* Location - Hidden on mobile, visible on larger screens */}
                    <p className="hidden sm:flex text-xs md:text-sm lg:text-base text-gray-600 mb-2 md:mb-3 items-center">
                      <i className="ri-map-pin-line mr-1 text-blue-600"></i>
                      {property.location}
                    </p>
                    
                    <div className="flex items-center justify-between mb-2 sm:mb-3 md:mb-4">
                      <p className="text-sm sm:text-lg md:text-xl lg:text-2xl xl:text-3xl font-bold text-blue-600">{priceText}</p>
                      <div className="flex items-center space-x-2">
                        <p className="text-xs sm:text-sm text-gray-500">({property.total_reviews})</p>
                        {/* Quick indicator next to price */}
                        {availabilityBadge(property.status)}
                        {priceTypeBadge(property.display_price_label)}
                      </div>
                    </div>
                    
                    {/* Features - Responsive grid */}
                    <div className="grid grid-cols-1 xs:grid-cols-2 gap-1 sm:gap-1.5 md:gap-2">
                      {features.map((feature, index) => (
                        <div key={index} className="flex items-center text-xs sm:text-sm text-gray-600">
                          <i className="ri-check-line text-green-500 mr-1 text-xs sm:text-sm flex-shrink-0"></i>
                          <span className="truncate">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Car Selling Section (only under Cars tab) */}
        {activeCategory === 'cars' && (<CarSellingSection properties={allProperties} />)}
      </div>
    </section>
  );
};

export const CarSellingSection: React.FC<{ properties: PublicProperty[] }> = ({ properties }) => {
  const carsForSale = properties.filter(p => (p.category || '').toLowerCase() === 'car' && (p.display_price_label || '').toLowerCase() === 'full');
  return (
    <section className="mt-8 sm:mt-10">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold">Car Selling</h2>
        <a href="/properties?category=car" className="text-blue-600 hover:text-blue-700 text-sm">See all cars</a>
      </div>
      {carsForSale.length === 0 ? (
        <div className="text-center py-10 border border-gray-200 rounded-xl bg-white">
          <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900">No property listed</h3>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {carsForSale.map(car => (
            <a key={car.id} href={`/property-details/car/${car.id}`} className="block group border border-gray-200 rounded-xl overflow-hidden bg-white">
              <div className="aspect-video bg-gray-100 overflow-hidden">
                <img src={(Array.isArray(car.images) && car.images[0]) || car.image_url || ''} alt={car.title || car.display_name || 'Car'} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
              </div>
              <div className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm sm:text-base font-semibold text-gray-900 truncate">{car.title || car.display_name || 'Car'}</h3>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-red-600 text-white">FOR SALE</span>
                </div>
                <p className="mt-1 text-blue-600 font-semibold">
                  {typeof car.display_price === 'number' ? car.display_price.toLocaleString() : ''}
                </p>
                {car.location && <p className="text-xs sm:text-sm text-gray-600 truncate"><i className="ri-map-pin-line mr-1"></i>{car.location}</p>}
              </div>
            </a>
          ))}
        </div>
      )}
    </section>
  );
};
